import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { MatIconButton } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatTooltip } from '@angular/material/tooltip';
import { uniqBy } from 'lodash';
import { Subscription, firstValueFrom } from 'rxjs';
import { TenancyService } from '../../features/object/project/tenancy/tenancy.service';
import { ModelRole } from '../../models/model-role';
import { Tenancy } from '../../models/tenancy';
import { snackErr } from '../../modules/snack.module';
import { BoxModule } from '../box/box.module';
import { ModelRoleService } from '../model-role/model-role.service';
import { translateKnownModelRoleRoles } from '../model-role/model-role.translated';
import { t } from '../translate/translate.function';
import { TranslateModule } from '../translate/translate.module';
import { SelectTenantInvolvedDialogComponent } from './select-tenant-involved-dialog.component';

class ModelRolesExtended extends ModelRole {
  translatedRole?: string;
}

@Component({
  selector: 'apex-object-tenant-selector',
  templateUrl: './object-tenant-selector.component.html',
  standalone: true,
  imports: [CommonModule, MatIcon, MatProgressSpinner, MatTooltip, MatIconButton, TranslateModule, BoxModule],
  providers: [TenancyService],
})
export class ObjectTenantSelectorComponent implements OnInit, OnDestroy {
  @Input() objectId: number;
  @Input() agreementId: number;
  @Input() disabled = false;

  @Output() tenantAdded = new EventEmitter<ModelRole>();

  availableTenants: ModelRolesExtended[] = [];
  loadingTenants = false;
  involvedRoles: ModelRole[] = [];

  private subscription = new Subscription();

  constructor(
    private tenancyService: TenancyService,
    private modelRoleService: ModelRoleService,
    private dialog: MatDialog,
  ) {}

  ngOnInit(): void {
    if (this.objectId && this.agreementId) {
      void this.fetchTenantsFromObject();
      void this.fetchInvolvedRoles();

      // Subscribe to role changes to update button states when roles are added/removed
      this.subscription.add(
        this.modelRoleService.roleChanged$.subscribe({
          next: (change) => {
            // Only refresh if the change is for this agreement
            if (change.model === 'agreement' && String(change.modelId) === String(this.agreementId)) {
              void this.fetchInvolvedRoles();
            }
          },
        }),
      );
    }
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  async fetchTenantsFromObject(): Promise<void> {
    try {
      this.loadingTenants = true;

      if (!this.objectId) {
        this.availableTenants = [];

        return;
      }

      // Get all tenancies for the linked object
      const tenanciesResponse = await firstValueFrom(this.tenancyService.queryWithObject(this.objectId));
      const tenancies: Tenancy[] = tenanciesResponse.Collection || [];

      // For each tenancy, get all tenants
      const allTenantRoles: ModelRole[] = [];

      for (const tenancy of tenancies) {
        if (tenancy.id) {
          try {
            const tenantRoles = await firstValueFrom(
              this.modelRoleService.queryRoleForModel('tenancy', tenancy.id, 'tenant'),
            );

            allTenantRoles.push(...tenantRoles);
          } catch (err) {
            // Continue if a tenancy has no tenants
            console.warn(`No tenants found for tenancy ${tenancy.id}`, err);
          }
        }
      }

      // Remove duplicates based on CRMUserId or UserId and add translated role
      const uniqueTenants = uniqBy(
        allTenantRoles,
        (tenant) => tenant.CRMUserId || tenant.UserId || `${tenant.User?.id}-${tenant.CRMUser?.id}`,
      );

      this.availableTenants = uniqueTenants.map((tenant) => {
        const extendedTenant: ModelRolesExtended = new ModelRolesExtended(tenant);

        extendedTenant.translatedRole = t(translateKnownModelRoleRoles[tenant.role]);

        return extendedTenant;
      });
    } catch (error) {
      console.error('Error fetching tenants from object:', error);
      snackErr(t('Could not load tenants'), error);
    } finally {
      this.loadingTenants = false;
    }
  }

  async fetchInvolvedRoles(): Promise<void> {
    try {
      this.involvedRoles = await firstValueFrom(
        this.modelRoleService.queryRoleForModel('agreement', this.agreementId, 'involved'),
      );
    } catch (error) {
      console.error('Error fetching involved roles:', error);
      this.involvedRoles = [];
    }
  }

  isTenantAlreadyInvolved(tenant: ModelRole): boolean {
    return this.involvedRoles.some(
      (involved) =>
        (tenant.CRMUserId && involved.CRMUserId === tenant.CRMUserId) ||
        (tenant.UserId && involved.UserId === tenant.UserId),
    );
  }

  openAddTenantDialog(tenant: ModelRole): void {
    const dialogRef = this.dialog.open(SelectTenantInvolvedDialogComponent, {
      data: {
        tenant,
        agreementId: this.agreementId,
      },
    });

    this.subscription.add(
      dialogRef.afterClosed().subscribe({
        next: async (result) => {
          if (result) {
            // Refresh the involved list to get the latest data
            // This ensures the button state updates correctly
            await this.fetchInvolvedRoles();

            // Emit event so parent can show notification
            this.tenantAdded.emit(result);
          }
        },
      }),
    );
  }
}
